/**
 * Utilities for handling Rarible CDN URLs and extracting original media URLs
 */

/**
 * Checks if a URL is a Rarible CDN thumbnail/preview URL
 */
export function isRaribleThumbnailUrl(url?: string): boolean {
  if (!url) return false
  const lowerUrl = url.toLowerCase()
  return (
    lowerUrl.includes("assets.raribleuserdata.com") &&
    (lowerUrl.includes("t_video_thumb") ||
      lowerUrl.includes("t_image_thumb") ||
      lowerUrl.includes("/image/") ||
      lowerUrl.includes("/video/"))
  )
}

/**
 * Extracts the original media URL from a Rarible CDN thumbnail URL
 * Rarible encodes original URLs in base64 as the last path segment
 */
export function extractOriginalMediaUrl(thumbnailUrl: string): string | null {
  try {
    // Check if it's a Rarible CDN URL
    if (!isRaribleThumbnailUrl(thumbnailUrl)) {
      return null
    }

    // Extract the base64 encoded part (last segment of the path)
    const url = new URL(thumbnailUrl)
    const pathSegments = url.pathname.split("/")

    // For URLs like /prod/v1/image/c_limit,g_center,h_1260,w_1260,f_auto,q_auto/[base64]
    // or /prod/v1/video/t_video_thumb_display/[base64]
    // The base64 is always the last segment
    const encodedUrl = pathSegments[pathSegments.length - 1]

    // Skip if the last segment looks like a transformation parameter (but allow = for base64 padding)
    if (encodedUrl.includes(",") || encodedUrl.length < 20) {
      return null
    }

    // Decode the base64 URL
    const decodedUrl = atob(encodedUrl)

    // Validate that it's a proper URL
    new URL(decodedUrl) // This will throw if invalid

    return decodedUrl
  } catch (error) {
    return null
  }
}

/**
 * Gets the best media URL - either original or thumbnail fallback
 * For videos, always try to use the original URL (even without extension)
 * since the thumbnail is just a JPEG preview
 */
export function getBestMediaUrl(url?: string): string | undefined {
  if (!url) return undefined

  const originalUrl = extractOriginalMediaUrl(url)

  // Always prefer original URL if available, even without extension
  // For videos, the original IPFS URL contains the actual video
  // while the CDN URL is just a thumbnail image
  return originalUrl || url
}

/**
 * Gets the best poster/thumbnail URL for videos
 * Returns the thumbnail URL if it's a video, or generates one from the original URL
 */
export function getVideoPosterUrl(
  originalThumbnailUrl?: string,
  originalVideoUrl?: string,
): string | undefined {
  if (!originalThumbnailUrl && !originalVideoUrl) return undefined

  // If we have a Rarible thumbnail URL that contains video transformations, use it as poster
  if (originalThumbnailUrl && isRaribleThumbnailUrl(originalThumbnailUrl)) {
    // For video thumbnails, the thumbnail URL itself is perfect as a poster
    if (
      originalThumbnailUrl.includes("/video/") ||
      originalThumbnailUrl.includes("t_video_thumb")
    ) {
      return originalThumbnailUrl
    }
  }

  // If we have the original video URL, we could potentially generate a thumbnail
  // For now, return undefined to use the video's first frame
  return undefined
}

/**
 * Detects if the original URL (not thumbnail) is a video
 */
export function isOriginalVideo(thumbnailUrl?: string): boolean {
  if (!thumbnailUrl) return false

  const originalUrl = extractOriginalMediaUrl(thumbnailUrl)
  if (!originalUrl) return false

  return (
    originalUrl.toLowerCase().includes(".mp4") ||
    originalUrl.toLowerCase().includes(".webm") ||
    originalUrl.toLowerCase().includes(".mov")
  )
}

/**
 * Detects if the original URL is a 3D model
 */
export function isOriginal3D(thumbnailUrl?: string): boolean {
  if (!thumbnailUrl) return false

  const originalUrl = extractOriginalMediaUrl(thumbnailUrl)
  if (!originalUrl) return false

  return (
    originalUrl.toLowerCase().includes(".glb") ||
    originalUrl.toLowerCase().includes(".gltf")
  )
}
